/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { getEntityRoles } from 'src/luis/projects/entities/utils/entityHelpers';
import { BatchTest } from 'src/luis/projects/testPane/types/BatchTest';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { BatchTestResultItem } from 'src/luis/types/stores/testPane/BatchTestResultItem';
import { ConfusionType } from 'src/luis/types/stores/testPane/ConfusionType';

export type ModelConfusions = Map<LuisId, ConfusionAggregate>;

export type ConfusionAggregate = {
    tp: number;
    fp: number;
    tn: number;
    fn: number;
    scores: Map<LuisId, { confusion: ConfusionType; score: number }[]>;
};

export const emptyAggregates = (): ConfusionAggregate => ({ tp: 0, fp: 0, tn: 0, fn: 0, scores: new Map() });

/**
 * Gets the overall number of utterances that passed both
 * in intents, entities and roles.
 *
 * @returns An observable of the count.
 */
export const getCorrectCount = (resultItems: BatchTestResultItem[], entities: EntityStore[], withPrebuilts: boolean = false) => {
    const entityRoles = flatten(entities.map(e => e.entityRoleStoreHouse.items));

    const ids = (withPrebuilts
        ? [...entityRoles, ...entities]
        : [...entityRoles, ...entities.filter(e => e.typeId !== ModelType.Prebuilt)]
    ).map(e => e.id);

    return getCorrectResultItems(resultItems, ids);
};

/**
 * Gets the confusion scores for each model aggregated
 * on all the utterances of this dataset.
 *
 * @returns A map of model id and confusion aggregation
 * object that contains the count of each confusion type
 * this model registered for each utterance.
 */
export const getModelConfusions = (batchTest: BatchTest, entities: Entity[], withPrebuilts: boolean): ModelConfusions =>
    new Map<LuisId, ConfusionAggregate>([
        ...getIntentConfusions(batchTest.resultItems),
        ...getEntityConfusions(batchTest.resultItems, entities, withPrebuilts),
        ...getEntityConfusions(batchTest.resultItems, getEntityRoles(entities), false)
    ]);

/**
 * Gets the total confusion counts for each model that was
 * tested across all of the dataset utterances.
 *
 * @returns A map of the model if and the count of all the confusions for that model.
 */
export const getAllConfusionCount = (aggregates: ModelConfusions): Map<LuisId, number> => {
    const getTotalCount = (id: string, map: ModelConfusions) => map.get(id).tp + map.get(id).tn + map.get(id).fp + map.get(id).fn;

    return aggregateConfusions(aggregates, getTotalCount);
};

/**
 * Gets the correct confusion counts for each model that was
 * tested across all of the dataset utterances.
 *
 * @returns A map of model id and the count of all the correct
 * confusions that were correct.
 */
export const getCorrectConfusionsCount = (aggregates: ModelConfusions): Map<LuisId, number> => {
    const getCorrectConfusionCount = (id: string, map: ModelConfusions) => map.get(id).tp + map.get(id).tn;

    return aggregateConfusions(aggregates, getCorrectConfusionCount);
};

/**
 * Gets the overall number of correct utterances that passed both
 * intents, entities and roles confusions. if an utterance fails in any of
 * them along the way then it is not counted.
 *
 * @param items The results from the dataset running.
 * @param entityIdsToInclude The ids to check from the confusion scores.
 * This parameter is useful when we want to exclude some entities from
 * the score calculations, eg: prebuilt entities.
 * @returns A count of the overall number of utterances that succeeded fully.
 */
const getCorrectResultItems = (items: BatchTestResultItem[], entityIdsToInclude: LuisId[]): number =>
    items.reduce((acc, item) => {
        if (item.labeledIntent.name !== item.predictedIntents[0].intent.name) {
            return acc;
        }

        const entityIds = Array.from(item.entityConfusions.keys()).filter(id => entityIdsToInclude.indexOf(id) !== -1);

        if (entityIds.length > 0) {
            const incorrectConfusion = entityIds
                .map(id => item.entityConfusions.get(id))
                .reduce((a, b) => a.concat(b))
                .find(c => c.typeId === ConfusionType.FalsePositive || c.typeId === ConfusionType.FalseNegative);

            if (incorrectConfusion !== undefined) {
                return acc;
            }
        }

        return acc + 1;
    }, 0);

/**
 * Aggregates the confusion scores for each intent for each item. That is,
 * this function creates a map of intent id and a confusion aggregation
 * object that contains the counts of all true positives, true negatives,
 * etc. from all the utterances that were included in the dataset.
 *
 * @param items The items that resulted from the dataset being run
 * @returns A map of intent id and the confusion aggregation object.
 */
const getIntentConfusions = (items: BatchTestResultItem[]): ModelConfusions =>
    items.reduce((acc, item) => {
        item.predictedIntents.forEach((pI, index) => {
            const result: ConfusionAggregate = acc.get(pI.intent.id) || emptyAggregates();
            let confusion: number;

            if (pI.intent.id === item.labeledIntent.id && index === 0) {
                // Intent was labeled and predicted correctly. True positive.

                result.tp += 1;
                confusion = ConfusionType.TruePositive;
            } else if (pI.intent.id === item.labeledIntent.id && index !== 0) {
                // Intent was labeled but predicted incorrectly. False negative.

                result.fn += 1;
                confusion = ConfusionType.FalseNegative;
            } else if (pI.intent.id !== item.labeledIntent.id && index === 0) {
                // Intent was not labeled and was predicted incorrectly. False positive.

                result.fp += 1;
                confusion = ConfusionType.FalsePositive;
            } else if (pI.intent.id !== item.labeledIntent.id && index !== 0) {
                // Intent was not labeled and was not predicted. True negative.

                result.tn += 1;
                confusion = ConfusionType.TrueNegative;
            }

            result.scores.set(item.id, [{ score: pI.score, confusion }]);
            acc.set(pI.intent.id, result);
        });

        return acc;
    }, new Map<LuisId, ConfusionAggregate>());

/**
 * Aggregates the confusion scores for each entity or role for each item. That is,
 * this function creates a map of model id and a confusion aggregation
 * object that contains the counts of all true positives, true negatives,
 * etc. from all the utterances that were included in the dataset.
 *
 * @param items The items that resulted from the dataset being run
 * @returns A map of model (entity or role) id and the confusion aggregation object.
 */
const getEntityConfusions = (items: BatchTestResultItem[], models: (Entity | EntityRole)[], withPrebuilts: boolean): ModelConfusions =>
    items.reduce((acc, item) => {
        Array.from(item.entityConfusions.keys()).map(eId => {
            const modelMatch = models.find(e => e.id === eId);

            if (!modelMatch) {
                return acc;
            }

            const modelTypeId = (<Entity>modelMatch).typeId;

            if (!withPrebuilts && modelTypeId === ModelType.Prebuilt) {
                return acc;
            }

            const result: ConfusionAggregate = acc.get(eId) || emptyAggregates();

            item.entityConfusions.get(eId).forEach(cI => {
                switch (cI.typeId) {
                    case ConfusionType.TruePositive:
                        result.tp += 1;
                        break;
                    case ConfusionType.TrueNegative:
                        result.tn += 1;
                        break;
                    case ConfusionType.FalsePositive:
                        result.fp += 1;
                        break;
                    case ConfusionType.FalseNegative:
                        result.fn += 1;
                        break;
                    default:
                }
            });

            result.scores.set(
                item.id,
                item.entityConfusions.get(eId).map(cI => ({ score: cI.score, confusion: cI.typeId }))
            );

            acc.set(eId, result);
        });

        return acc;
    }, new Map<string, ConfusionAggregate>());

/**
 * Aggregates the confusions to model id and a count, according to the given
 * aggregation function.
 *
 * @param map The map of confusions.
 * @param func The function to use to aggregate the confusions.
 * @returns A map of the model id and the aggregated confusion scores.
 */
const aggregateConfusions = (map: ModelConfusions, func: (id: LuisId, map: ModelConfusions) => number): Map<LuisId, number> => {
    const ids = <LuisId[]>Array.from(map.keys());

    return ids.reduce((acc, id) => acc.set(id, func(id, map)), new Map<LuisId, number>());
};
